import React, { createContext, useContext } from "react";
import { useGlobalErrorHandler } from "../hooks/useGlobalErrorHandler";
import { useGlobalLoading as useGlobalLoadingHook } from "../hooks/useGlobalLoading";
import ErrorMessage from "../components/common/ErrorMessage";
import LoadingOverlay from "../components/common/LoadingOverlay";

// Create contexts
const GlobalErrorContext = createContext();
const GlobalLoadingContext = createContext();

/**
 * Global State Provider that manages error and loading states across the application
 */
export const GlobalStateProvider = ({ children }) => {
  const errorHandler = useGlobalErrorHandler();
  const loadingManager = useGlobalLoadingHook();

  return (
    <GlobalErrorContext.Provider value={errorHandler}>
      <GlobalLoadingContext.Provider value={loadingManager}>
        {children}

        {/* Global Error Display */}
        <GlobalErrorDisplay />

        {/* Global Loading Overlay */}
        <GlobalLoadingDisplay />
      </GlobalLoadingContext.Provider>
    </GlobalErrorContext.Provider>
  );
};

/**
 * Global Error Display Component
 * Shows all global errors in a fixed position
 */
const GlobalErrorDisplay = () => {
  const { errors, removeError } = useGlobalError();

  if (errors.length === 0) return null;

  return (
    <div className="global-error-container">
      {errors.map((error) => (
        <div key={error.id} className="global-error-item">
          <ErrorMessage
            message={error.message}
            onDismiss={() => removeError(error.id)}
            type={error.type}
            details={error.details}
            context={error.context}
          />
        </div>
      ))}
    </div>
  );
};

/**
 * Global Loading Display Component
 * Shows global loading overlay when any operation is loading
 */
const GlobalLoadingDisplay = () => {
  const { isAnyLoading, globalMessage, getLoadingOperations } =
    useGlobalLoadingContext();

  if (!isAnyLoading()) return null;

  const operations = getLoadingOperations();
  const longestRunning = operations.reduce(
    (longest, current) =>
      current.duration > longest.duration ? current : longest,
    operations[0] || { duration: 0 },
  );

  // Show timeout warning for operations running longer than 15 seconds
  const showTimeout = longestRunning.duration > 15000;

  return (
    <LoadingOverlay
      isVisible={true}
      message={globalMessage}
      fullScreen={true}
      size="large"
      showProgress={false}
      timeout={showTimeout ? 0 : undefined}
      onTimeout={() => {
        console.warn("Long running operation detected:", longestRunning);
      }}
    />
  );
};

/**
 * Hook to access global error handling
 */
export const useGlobalError = () => {
  const context = useContext(GlobalErrorContext);
  if (!context) {
    throw new Error("useGlobalError must be used within a GlobalStateProvider");
  }
  return context;
};

/**
 * Hook to access global loading management
 */
export const useGlobalLoadingContext = () => {
  const context = useContext(GlobalLoadingContext);
  if (!context) {
    throw new Error(
      "useGlobalLoadingContext must be used within a GlobalStateProvider",
    );
  }
  return context;
};

/**
 * Combined hook for both error and loading management
 */
export const useGlobalState = () => {
  const errorHandler = useGlobalError();
  const loadingManager = useGlobalLoadingContext();

  return {
    ...errorHandler,
    ...loadingManager,

    // Combined utilities
    withErrorHandling: async (
      operation,
      context = "",
      loadingKey = null,
      loadingMessage = "Loading...",
    ) => {
      try {
        if (loadingKey) {
          loadingManager.startLoading(loadingKey, loadingMessage);
        }

        const result = await operation();
        return result;
      } catch (error) {
        errorHandler.handleError(error, context);
        throw error;
      } finally {
        if (loadingKey) {
          loadingManager.stopLoading(loadingKey);
        }
      }
    },
  };
};

export default GlobalStateProvider;
